import React, { useState, useEffect, useRef } from 'react'
import { Select } from 'antd'
import CompanyApi from '../../../services/company.api'
import { successOrFailureToaster } from '../atoms/messages'

const { Option } = Select

const CompanySearchSelect = ({
  mode = 'single',
  value,
  onSelect,
  onChange,
  placeholder = 'Select company',
  filterResult,
  initialIds,
  initialOptions,
  style
}) => {
  const [term, setTerm] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [companyMap, setCompanyMap] = useState({})
  const inputRef = useRef(null)
  const popupRef = useRef(null)

  const applyFilter = (list) =>
    typeof filterResult === 'function' ? list.filter(filterResult) : list

  const addToMap = (companies) =>
    setCompanyMap((prev) => {
      const next = { ...prev }
      companies.forEach((c) => { next[c.id] = c })
      return next
    })

  const runSearch = () => {
    if (!term.trim()) return
    setLoading(true)
    CompanyApi.searchCompanies(
      { q: term.trim() },
      (res) => {
        const companies = res.data.companies || []
        setOptions(applyFilter(companies))
        addToMap(companies)
        setLoading(false)
      },
      () => {
        successOrFailureToaster({ message: 'Company search failed', type: 'error' })
        setLoading(false)
      }
    )
  }

  useEffect(() => {
    if (Array.isArray(initialOptions) && initialOptions.length) {
      setOptions(applyFilter(initialOptions))
      addToMap(initialOptions)
      return
    }
    if (Array.isArray(initialIds) && initialIds.length) {
      CompanyApi.searchCompanies(
        { ids: initialIds },
        (res) => {
          const companies = res.data.companies || []
          setOptions(applyFilter(companies))
          addToMap(companies)
        },
        () => {}
      )
    }
  }, [])

  // The dropdown's open state is fully controlled here. antd's rc-select
  // programmatically refocuses its own input whenever you interact inside the
  // dropdown (clicking the search box, the Search button, or an option), which
  // fires a spurious onDropdownVisibleChange(false). preventDefault cannot stop
  // a programmatic focus, so instead we never let antd close the dropdown: we
  // only honour its request to OPEN, and own every close path ourselves —
  // a real outside click (below), or selecting a company (handleChange, single
  // mode). This makes focus churn inside the dropdown irrelevant.
  useEffect(() => {
    if (!open) return undefined
    const handleOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  // Focus the search box when the dropdown opens. Safe now that antd can't
  // blur-close us.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 50)
      return () => clearTimeout(t)
    }
    return undefined
  }, [open])

  const dropdownRender = (menu) => (
    <div ref={popupRef}>
      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: '8px 8px 7px',
          borderBottom: '1px solid #E5E7EB'
        }}
      >
        <input
          ref={inputRef}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); runSearch() }
            if (e.key === 'Escape') { e.preventDefault(); setOpen(false) }
          }}
          placeholder='Search by name…'
          style={{
            flex: 1,
            height: 30,
            padding: '0 8px',
            fontSize: 13,
            border: '1px solid #CBD5E1',
            borderRadius: 6,
            outline: 'none',
            background: '#F8FAFC',
            minWidth: 0
          }}
        />
        <button
          type='button'
          onClick={runSearch}
          style={{
            height: 30,
            padding: '0 12px',
            fontSize: 13,
            fontWeight: 600,
            color: '#fff',
            background: loading ? '#BFDBFE' : '#2563EB',
            border: 'none',
            borderRadius: 6,
            cursor: loading ? 'default' : 'pointer',
            flexShrink: 0,
            whiteSpace: 'nowrap'
          }}
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>
      <div>{menu}</div>
    </div>
  )

  const handleChange = (val, opt) => {
    if (mode === 'multi') {
      const picked = (Array.isArray(opt) ? opt : [opt])
        .map((o) => o && o.props && o.props.company)
        .filter(Boolean)
      if (onChange) onChange(val, picked)
    } else {
      setOpen(false)
      if (onSelect) onSelect(val, opt && opt.props && opt.props.company)
    }
  }

  const selectedIds = Array.isArray(value) ? value : (value ? [value] : [])
  const optionIds = new Set(options.map((c) => c.id))
  const extraSelectedCompanies = selectedIds
    .map((id) => companyMap[id])
    .filter((c) => c && !optionIds.has(c.id))
  const renderedCompanies = [...options, ...extraSelectedCompanies]

  return (
    <Select
      mode={mode === 'multi' ? 'multiple' : undefined}
      open={open}
      onDropdownVisibleChange={(visible) => { if (visible) setOpen(true) }}
      style={{ minWidth: 200, ...style }}
      value={value}
      placeholder={placeholder}
      filterOption={false}
      notFoundContent={loading ? 'Searching…' : 'Type a name and click Search'}
      dropdownRender={dropdownRender}
      onChange={handleChange}
    >
      {renderedCompanies.map((c) => (
        <Option key={c.id} value={c.id} company={c}>
          {c.name}
        </Option>
      ))}
    </Select>
  )
}

export default CompanySearchSelect
